import { Injectable, inject } from '@angular/core';
import { DropEvent } from '@interactjs/actions/drop/DropEvent';
import { DropzoneOptions } from '@interactjs/actions/drop/plugin';
import { SnapOptions } from '@interactjs/modifiers/snap/pointer';
import interact from 'interactjs';
import { Subject } from 'rxjs';
import { BaseCalendarModel } from '../../../../../../../../../core/models/calendar/base-calendar.model';
import { TimeBlockMonthVariableIntervalType } from '../../../../../../../../../core/models/timeblock/time-block-view-type.model';
import { ShiveRenderService } from '../../../../../../../../../core/services/rendering/shive-render.service';
import { InteractCallbackFns } from '../../../../../../../../../shared/data-types/interact.types';
import Autobind from '../../../../../../../../../shared/typescript-decorators/autobind.decorator';
import { CalendarGeometryService } from '../../../../../../../services/calendar-geometry.service';
import { CalendarServiceHelper } from '../../../../../../../services/calendar-service-helper';
import {
  CSSCanDropClass,
  CSSDropActiveClass,
  CSSDropOkClass,
  CSSDropTargetClass,
  CssDropzone,
  HorizontalDragTolerance,
} from '../../../../../../../util/calendar-style.constants';
import { TimeBlockItemBuilderService } from '../../../../../generation/time-block-item-builder.service';
import { ITimeBlockComponentItem } from '../../../../../types/time-block-component-item-types';
import { InteractHandlerService } from '../../../interact-handler.service';
import { DragPart } from '../../drag-part';

@Injectable()
export class DragAndDropHandlerService {
  public sourceContainerId: number;
  public targetContainerId: number;
  public dragStarted$ = new Subject<DragPart>();

  private calendarModel: BaseCalendarModel;
  private timeBlockComponent: ITimeBlockComponentItem;

  // DI
  private readonly timeBlockItemBuilderService = inject(TimeBlockItemBuilderService);
  private readonly calendarGeometryService = inject(CalendarGeometryService);
  private readonly interactHandlerService = inject(InteractHandlerService);
  private readonly shiveRenderService = inject(ShiveRenderService);

  // Misc
  private readonly dragToggleLaneInterval = 5;

  /**
   * Initialize drag and its corresponding event handler. Restriction of vertical time blocks is done via drag function.
   * Remark: Snapping does not work for horizontal time blocks since the onDragEnter callback is not triggered because
   * the 'overlap' property in the initDrop() function cannot be set correctly to fit our purposes.
   * Furthermore, month view inner day time blocks are also snapped manually in the drag() function.
   */
  public initDrag(
    calendarModel: BaseCalendarModel,
    tbComponent: ITimeBlockComponentItem,
    dragPart: DragPart,
    callbackFns?: InteractCallbackFns,
  ): void {
    this.calendarModel = calendarModel;
    this.timeBlockComponent = tbComponent.clone(this.timeBlockItemBuilderService);
    const timeBlockModel = this.timeBlockComponent.timeBlockModel;

    const targetFn =
      timeBlockModel.fixInterval !== null ||
      timeBlockModel.timeBlockViewType instanceof TimeBlockMonthVariableIntervalType
        ? null
        : this.setTargetForVerticalBlock;
    const snapOptions: SnapOptions = {
      targets: [targetFn],
      relativePoints: [{ x: 0, y: 0 }],
      endOnly: false,
      range: Infinity,
      offset: null,
      origin: null,
    };

    this.interactHandlerService.makeMovable(tbComponent, dragPart, snapOptions, null, callbackFns);
  }

  /**
   * Initialize drop and its corresponding event handler (only for inner day time blocks).
   */
  public initDrop(): void {
    const dropZoneOptions: DropzoneOptions = {
      overlap: this.timeBlockComponent.timeBlockModel.fixInterval !== null ? null : 0.5,
    };

    const onDropActivate = (event: DropEvent) => {
      this.shiveRenderService.addClass(event.target, CSSDropActiveClass);
    };

    let dragEnterLastCalled = Date.now();
    const onDragEnter = (event: DropEvent) => {
      const dragEnterNowCalled = Date.now();
      // Call this method every 50ms to reduce CPU consumption.
      if (dragEnterNowCalled - dragEnterLastCalled < this.dragToggleLaneInterval) {
        return;
      }

      dragEnterLastCalled = dragEnterNowCalled;
      const draggableElement = event.relatedTarget;
      const dropzoneElement = event.target;

      // feedback the possibility of a drop
      this.shiveRenderService.addClass(dropzoneElement, CSSDropTargetClass);
      this.shiveRenderService.addClass(draggableElement, CSSCanDropClass);

      // set the target container id (only for inner day time blocks)
      if (
        this.timeBlockComponent.timeBlockModel.fixInterval === null &&
        this.targetContainerId !== +event.target.id
      ) {
        // Check for vertical lane switch
        this.targetContainerId = +event.target.id;
      }

      this.calcDropCoordinate(event);
    };

    const dragLeaveLastCalled = Date.now();
    const onDragLeave = (event: DropEvent) => {
      const dragLeaveNowCalled = Date.now();
      // Call this method every 50ms to reduce CPU consumption.
      if (dragLeaveNowCalled - dragLeaveLastCalled < this.dragToggleLaneInterval) {
        return;
      }
      // remove the drop feedback style
      this.shiveRenderService.removeClass(event.target, CSSDropTargetClass);
      this.shiveRenderService.removeClass(event.relatedTarget, CSSCanDropClass);
      this.shiveRenderService.removeClass(event.relatedTarget, CSSDropOkClass);
    };

    const onDrop = (event: DropEvent) => {
      this.shiveRenderService.addClass(event.relatedTarget, CSSDropOkClass);
      // set the target container id only for vertical time blocks since for horizontal ones
      // we set them in the horizontal drag handler.
      if (this.timeBlockComponent.timeBlockModel.fixInterval === null) {
        this.targetContainerId = +event.target.id;
      }
      InteractHandlerService.dropCoordinate = null;
    };

    const onDropDeactivate = (event: DropEvent) => {
      this.shiveRenderService.removeClass(event.target, CSSDropActiveClass);
      this.shiveRenderService.removeClass(event.target, CSSDropTargetClass);
    };

    this.interactHandlerService.makeDroppable(
      `.${CssDropzone}`,
      dropZoneOptions,
      onDragEnter,
      onDragLeave,
      onDropActivate,
      onDrop,
      onDropDeactivate,
    );
  }

  private calcDropCoordinate(event?: Interact.DragEvent | Interact.DropEvent): void {
    if (!event) {
      return;
    }

    const laneElement = event.currentTarget as Interact.Element as HTMLElement;
    const leftBorderWidth = this.calendarGeometryService.getLaneLeftBorderWidth(laneElement);
    const dropRect = interact.getElementRect(laneElement);
    // Immediately set the correct drop coordinate to prevent flickering when drag starts
    InteractHandlerService.dropCoordinate = {
      x: Math.round(dropRect.left) + leftBorderWidth,
    };
  }

  @Autobind
  private setTargetForVerticalBlock(x, y): unknown {
    if (!InteractHandlerService.dropCoordinate || !this.calendarModel) {
      return;
    }

    let range = HorizontalDragTolerance;
    const leftBound = this.calendarModel.geometryData.calendarBodyOffsetLeft;

    // Calculate right bound
    const lastLane = document.querySelector('.item-container .lane.last');
    const lastLaneWidth = (lastLane as HTMLElement).offsetWidth;
    const calOffsetWidth = this.calendarModel.geometryData.calendarBodyWidth;
    const rightBound = leftBound + calOffsetWidth - lastLaneWidth;

    // Get lane width
    const laneWidth = this.calendarGeometryService.getDayOrWeekVariableIntervalLaneWidth();
    // Check if time block stays within the calendar boundaries. Otherwise, snap it to nearest drop coordinate.
    if (x < leftBound - laneWidth + range) {
      range = Infinity;
      InteractHandlerService.dropCoordinate.x = leftBound - laneWidth;
      this.targetContainerId =
        CalendarServiceHelper.getFirstVisibleLaneIndex(this.calendarModel) - 1;
    } else if (x > rightBound + laneWidth - range) {
      range = Infinity;
      InteractHandlerService.dropCoordinate.x = rightBound + laneWidth;
      this.targetContainerId =
        CalendarServiceHelper.getLastVisibleLaneIndex(this.calendarModel) + 1;
    }
    return {
      x: InteractHandlerService.dropCoordinate.x,
      y,
      range,
    };
  }
}
